import { useState } from "react";
import { products } from "../../../mocks/products";
import ProductDetailModal from "./ProductDetailModal";

const ProductsSection = () => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const openModal = (product: typeof products[0], index: number) => {
    setSelectedProduct(product);
    setSelectedIndex(index);
  };

  const closeModal = () => setSelectedProduct(null);

  return (
    <>
      <section id="products" className="py-14 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="px-5 lg:px-10 mb-6 sm:mb-16">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div>
                <span className="text-orange-500 text-xs sm:text-sm font-bold tracking-widest uppercase block mb-2">
                  — PRODUCTS
                </span>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900 leading-tight">
                  삼성콘크리트 <span className="text-gray-400">제품 라인업</span>
                </h2>
              </div>
              <p className="hidden sm:block text-gray-500 text-sm max-w-md leading-relaxed">
                다양한 현장 환경과 설계 요구에 맞춘 5가지 전문 옹벽 블록 제품군을 제공합니다.
              </p>
            </div>
          </div>

          {/* 모바일 전용: 컴팩트 2열 그리드 */}
          <div className="block sm:hidden px-5">
            <div className="grid grid-cols-2 gap-2.5">
              {products.map((product, index) => (
                <div
                  key={product.id}
                  className="relative bg-gray-50 rounded-xl overflow-hidden cursor-pointer"
                  onClick={() => openModal(product, index)}
                >
                  <div className="absolute top-2 left-2 z-10 w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center">
                    <span className="text-white font-black text-xs">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="w-full h-24 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-2.5">
                    <h3 className="text-xs font-black text-gray-900 leading-tight mb-1">
                      {product.name}
                    </h3>
                    <p className="text-gray-400 text-xs leading-snug line-clamp-2">
                      {product.shortDesc}
                    </p>
                    <div className="flex items-center gap-1 text-orange-500 text-xs font-bold mt-2">
                      자세히 <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </div>
              ))}

              {/* 견적 문의 카드 */}
              <div
                className="bg-gray-900 rounded-xl p-3 flex flex-col justify-between cursor-pointer"
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div className="w-7 h-7 bg-orange-500/20 rounded-lg flex items-center justify-center mb-2">
                  <i className="ri-customer-service-2-line text-orange-500 text-sm"></i>
                </div>
                <div>
                  <h3 className="text-xs font-black text-white leading-tight mb-1">
                    현장 맞춤<br />견적 문의
                  </h3>
                  <div className="flex items-center gap-1 text-orange-500 text-xs font-bold mt-2">
                    문의하기 <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 데스크탑 그리드 */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 px-6 lg:px-10">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="group relative bg-gray-50 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                onMouseEnter={() => setHovered(product.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => openModal(product, index)}
              >
                <div className="absolute top-4 left-4 z-10 w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-black text-sm">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-black text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{product.shortDesc}</p>
                  <ul className="space-y-1.5 mb-5">
                    {product.features.map((feat) => (
                      <li key={feat} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-orange-500 text-sm"></i>
                        </div>
                        {feat}
                      </li>
                    ))}
                  </ul>
                  {/* 탭 미리보기 뱃지 */}
                  <div className="flex items-center gap-1.5 mb-4">
                    {["제품개요", "제품사양", "제품도면"].map((tab) => (
                      <span key={tab} className="text-xs px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full font-medium">
                        {tab}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-orange-500 text-sm font-bold group-hover:underline">자세히 보기</span>
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500/10 rounded-full group-hover:bg-orange-500 transition-colors">
                      <i className="ri-arrow-right-line text-orange-500 group-hover:text-white text-sm"></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`absolute inset-0 border-2 border-orange-500 rounded-2xl transition-opacity duration-300 pointer-events-none ${
                    hovered === product.id ? "opacity-100" : "opacity-0"
                  }`}
                ></div>
              </div>
            ))}

            <div className="bg-gray-900 rounded-2xl p-6 flex flex-col justify-between cursor-pointer group hover:-translate-y-1 transition-all duration-300">
              <div>
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-customer-service-2-line text-orange-500 text-xl"></i>
                </div>
                <h3 className="text-2xl font-black text-white mb-3">현장 맞춤<br />견적 문의</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  어떤 현장이든 최적의 제품과 솔루션을 제안해 드립니다.
                </p>
              </div>
              <button
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-8 flex items-center gap-2 text-orange-500 font-bold text-sm group-hover:gap-4 transition-all whitespace-nowrap cursor-pointer"
              >
                문의하기 <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <ProductDetailModal
        product={selectedProduct}
        productIndex={selectedIndex}
        onClose={closeModal}
      />
    </>
  );
};

export default ProductsSection;
